import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import useScrollLock from '../../hooks/useScrollLock';
import { useLanguage } from '../../contexts/LanguageContext';
import { printPtoDecision, emailPtoDecision } from '../../utils/printPtoDecision';
import './Salon.css';

const todayString = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

const PrintPtoDecisionModal = ({ isOpen, onClose, salonId, request, employee, approverName }) => {
  const { t, formatWeddingDate } = useLanguage();
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [printDate, setPrintDate] = useState(todayString());
  const [ptoYear, setPtoYear] = useState('');
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSentMessage, setEmailSentMessage] = useState('');
  const [error, setError] = useState('');

  useScrollLock(isOpen);

  const fetchTemplates = useCallback(async () => {
    if (!salonId) return;
    try {
      setLoadingTemplates(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/salons/${salonId}/pto/decision-templates`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { active_only: true }
      });
      const list = response.data.templates || [];
      setTemplates(list);
      setSelectedTemplateId(list.length > 0 ? String(list[0].id) : '');
    } catch (err) {
      console.error('Error fetching PTO decision templates:', err);
      setError(t('timeOff.pto.failedToLoadDecisionTemplates'));
    } finally {
      setLoadingTemplates(false);
    }
  }, [salonId, t]);

  // Default {{pto_year}} to the previous year if the employee has carried-over days available
  // at the start of the leave year, otherwise the leave year itself - matches the same rule
  // buildPtoDecisionPlaceholders falls back to when this isn't overridden.
  const fetchDefaultPtoYear = useCallback(async () => {
    if (!salonId || !request || !employee) return;
    const year = parseInt(request.start_date.split('-')[0], 10);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/salons/${salonId}/pto/balance`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { userId: employee.id, year }
      });
      const carriedOverDays = response.data.carriedOverDays || 0;
      setPtoYear(String(carriedOverDays > 0 ? year - 1 : year));
    } catch (err) {
      console.error('Error fetching PTO balance for default pto_year:', err);
      setPtoYear(String(year));
    }
  }, [salonId, request, employee]);

  useEffect(() => {
    if (isOpen) {
      setError('');
      setEmailSentMessage('');
      setPrintDate(todayString());
      fetchTemplates();
      fetchDefaultPtoYear();
    }
  }, [isOpen, fetchTemplates, fetchDefaultPtoYear]);

  const handlePrint = async () => {
    if (!selectedTemplateId || !request || !employee) return;

    try {
      setPrinting(true);
      setError('');
      await printPtoDecision({
        salonId,
        templateId: selectedTemplateId,
        request,
        employee,
        t,
        formatDate: formatWeddingDate,
        printDate,
        approverName,
        ptoYear
      });
      onClose();
    } catch (err) {
      console.error('Error printing PTO decision:', err);
      setError(err.message || t('timeOff.pto.failedToPrintDecision'));
    } finally {
      setPrinting(false);
    }
  };

  const handleSendEmail = async () => {
    if (!selectedTemplateId || !request || !employee) return;

    const selectedTemplate = templates.find((tpl) => String(tpl.id) === String(selectedTemplateId));
    const employeeName = `${employee.first_name || ''} ${employee.last_name || ''}`.trim();

    try {
      setSendingEmail(true);
      setError('');
      setEmailSentMessage('');
      const result = await emailPtoDecision({
        salonId,
        templateId: selectedTemplateId,
        request,
        employee,
        t,
        formatDate: formatWeddingDate,
        printDate,
        approverName,
        ptoYear,
        emailSubject: `${selectedTemplate?.title || t('timeOff.pto.printDecision')} - ${employeeName}`,
        emailBody: t('timeOff.pto.decisionEmailBodyDefault')
      });
      setEmailSentMessage(t('timeOff.pto.decisionEmailSent', 'Decision emailed to {name}', { name: result?.to || employee.email }));
    } catch (err) {
      console.error('Error emailing PTO decision:', err);
      setError(err.response?.data?.error || err.message || t('timeOff.pto.failedToSendDecisionEmail'));
    } finally {
      setSendingEmail(false);
    }
  };

  if (!isOpen) return null;

  const busy = printing || sendingEmail;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: '480px' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{t('timeOff.pto.printDecision')}</h3>
          <button type="button" className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {employee && (
            <p style={{ marginBottom: '16px' }}>
              <strong>{employee.first_name} {employee.last_name}</strong>
              {request ? ` — ${formatWeddingDate(request.start_date)} → ${formatWeddingDate(request.end_date)}` : ''}
            </p>
          )}
          {error && <div className="error-message">{error}</div>}
          {emailSentMessage && <div className="success-message">{emailSentMessage}</div>}
          {loadingTemplates ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>{t('common.loading')}...</p>
            </div>
          ) : templates.length === 0 ? (
            <p>{t('timeOff.pto.noDecisionTemplates')}</p>
          ) : (
            <>
              <div className="form-group">
                <label htmlFor="pto_decision_template_select">{t('timeOff.pto.decisionTemplate')}</label>
                <select
                  id="pto_decision_template_select"
                  value={selectedTemplateId}
                  onChange={(e) => setSelectedTemplateId(e.target.value)}
                >
                  {templates.map((tpl) => (
                    <option key={tpl.id} value={tpl.id}>{tpl.title}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="pto_decision_print_date">{t('timeOff.pto.printDate')}</label>
                <input
                  type="date"
                  id="pto_decision_print_date"
                  value={printDate}
                  onChange={(e) => setPrintDate(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label htmlFor="pto_decision_pto_year">{t('timeOff.pto.ptoYear')}</label>
                <input
                  type="number"
                  id="pto_decision_pto_year"
                  value={ptoYear}
                  onChange={(e) => setPtoYear(e.target.value)}
                />
              </div>
              {employee?.email && (
                <p style={{ fontSize: '13px', color: '#a0aec0', fontStyle: 'italic', margin: '0' }}>
                  {t('timeOff.pto.decisionEmailBccNote')}
                </p>
              )}
            </>
          )}
        </div>
        <div className="modal-footer">
          <button type="button" className="salon-button secondary" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="salon-button tertiary"
            onClick={handleSendEmail}
            disabled={busy || loadingTemplates || templates.length === 0 || !employee?.email}
            title={!employee?.email ? t('timeOff.pto.decisionEmailNoAddress') : undefined}
          >
            {sendingEmail ? t('timeOff.pto.sendingDecisionEmail') : t('timeOff.pto.sendDecisionEmail')}
          </button>
          <button
            type="button"
            className="salon-button primary"
            onClick={handlePrint}
            disabled={busy || loadingTemplates || templates.length === 0}
          >
            {printing ? `${t('common.loading')}...` : t('timeOff.pto.printDecision')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PrintPtoDecisionModal;
